import React, { useState, useEffect } from 'react';
import { Card, Button, ListGroup, Badge, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import { FaDownload, FaTrash } from 'react-icons/fa';
import messageService from '../../../services/messageService';
import { API_BASE_URL } from '../../../configs/apiConfig';
import MessageCompose from './MessageCompose';
import '../styles/MessageDetail.css';

const MessageDetail = ({ message, userId, onReply, loading, error }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replies, setReplies] = useState([]);
    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        if (message?.id) {
            loadReplies();
        }
    }, [message?.id]);

    const loadReplies = async () => {
        try {
            const replyData = await messageService.getReplies(message.id);
            setReplies(replyData);
        } catch (err) {
            console.error('Error loading replies:', err);
        }
    };

    const handleReplySent = async (reply) => {
        setShowReplyForm(false);
        await loadReplies();
        if (onReply) {
            onReply(reply);
        }
    };

    const handleDownloadAttachment = async (attachment) => {
        try {
            // Use the filePath directly to construct the file URL
            const fileUrl = `${API_BASE_URL}${attachment.filePath}`;
            
            const response = await fetch(fileUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });
            
            if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading attachment:', err);
            alert(`Download failed: ${err.message}`);
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        try {
            await messageService.deleteAttachment(attachmentId);
            window.location.reload();
        } catch (err) {
            console.error('Error deleting attachment:', err);
        }
    };

    if (!message) return null;
    if (loading) return <div className="p-3">Loading message...</div>;
    if (error) return <Alert variant="danger" className="m-3">{error}</Alert>;

    const formatDate = (date) => {
        return isMobile 
            ? format(new Date(date), 'MMM d, yyyy h:mm a')
            : format(new Date(date), 'PPpp');
    };

    return (
        <Card className="message-detail">
            <Card.Body>
                <div className="message-metadata mb-2">
                    <div className="d-flex justify-content-between align-items-top">
                        <div>
                            <strong>From:</strong> {message.senderName}<br />
                            <strong>To:</strong> {message.recipientName}
                            {message.ccRecipients?.length > 0 && (
                                <>
                                    <br />
                                    <strong>CC:</strong> {message.ccRecipients.map(cc => cc.userName).join(', ')}
                                </>
                            )}
                            <br />
                            <strong>Subject:</strong> {message.subject}
                        </div>
                        <div className="text-end">
                            <small className="text-muted">
                                {formatDate(message.createdAt)}
                            </small>
                        </div>
                    </div>
                </div>

                <div className="message-content" dangerouslySetInnerHTML={{ __html: message.content }} />

                {message.attachments?.length > 0 && (
                    <div className="message-content mt-2">
                        <strong>Attachments</strong>
                        <ListGroup variant="flush" className="mt-2">
                            {message.attachments.map(attachment => (
                                <ListGroup.Item key={attachment.id} className="d-flex justify-content-between align-items-center py-2 border-0">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-paperclip me-2"></i>
                                        <span className="text-break">{attachment.fileName}</span>
                                        <small className="text-muted ms-2">
                                            ({Math.round(attachment.fileSize / 1024)} KB)
                                        </small>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="p-0"
                                        onClick={() => handleDownloadAttachment(attachment)}
                                        title="Download"
                                    >
                                        <FaDownload className="text-primary" />
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </div>
                )}
            </Card.Body>

            {showReplyForm && (
                <Card.Footer className="p-3">
                    <MessageCompose
                        userId={userId}
                        replyTo={message}
                        onMessageSent={handleReplySent}
                    />
                </Card.Footer>
            )}

            {replies.length > 0 && (
                <Card.Footer className="p-3">
                    <h6 className="mb-3">Replies</h6>
                    <ListGroup variant="flush">
                        {replies.map(reply => (
                            <ListGroup.Item key={reply.id} className="reply-item">
                                <div className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong className="text-break">{reply.senderName}</strong>
                                        <small className="text-muted ms-2">
                                            {formatDate(reply.createdAt)}
                                        </small>
                                    </div>
                                    <div className="text-break" dangerouslySetInnerHTML={{ __html: reply.content }} />
                                    {reply.attachments?.length > 0 && (
                                        <div className="mt-2">
                                            <Badge bg="secondary">
                                                {reply.attachments.length} attachment(s)
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Card.Footer>
            )}
        </Card>
    );
};

export default MessageDetail; 